/**
 * Recurrence: a time's RFC 5545 rule (`record_times.rrule`) read, described
 * and expanded into the occurrences that fall in a range. A recurring time is
 * one row — its `start_at`/`end_at` are the first occurrence — so every reader
 * that asks "what happens between A and B" expands it here, the calendar and
 * the MCP alike.
 */

import type { RRule as RRuleType, RRuleSet as RRuleSetType } from "rrule"
import * as rruleModule from "rrule"

// rrule ships a CJS build whose only export is `default` (what Node ESM sees
// when the MCP runs core's dist) and an ESM build with only named exports
// (what bundlers and vitest pick), so `import { RRule } from "rrule"` fails
// under one or the other. Read the classes from whichever is there; the cast
// lets either resolution's types (NodeNext sees a `default`, bundlers don't)
// say `default` may be missing.
type RRuleModule = typeof rruleModule
const { RRule, rrulestr } =
  (rruleModule as RRuleModule & { default?: RRuleModule }).default ?? rruleModule

/** The fields of a time that expansion reads. */
type RecurringTime = { start_at?: string | null; end_at?: string | null; rrule?: string | null }

/** A time as a calendar draws it: the time itself, or one occurrence of a
 *  recurring one with its own start/end. */
export type ExpandedTime<T extends RecurringTime> = T & {
  /** Which occurrence in range this is (0 for a one-off time). */
  occurrence_index: number
  /** An occurrence's start, as a Date. */
  occurrence_date?: Date
  /** Set on an occurrence of a recurring time, never on a one-off. */
  __expanded?: true
}

/**
 * Parse an rrule string into an RRule (or an RRuleSet, when the string holds
 * several rules), anchored at `dtstart` when the string carries no DTSTART.
 * Null for an empty or unparseable rule.
 */
function parseRRule(
  rruleString: string | null | undefined,
  dtstart: Date | null = null
): RRuleType | RRuleSetType | null {
  if (!rruleString) return null

  try {
    // A bare rule ("FREQ=WEEKLY") runs from the time's own start.
    if (dtstart && !rruleString.includes("DTSTART")) {
      const rule = RRule.fromString(rruleString)
      return new RRule({ ...rule.origOptions, dtstart })
    }
    return rrulestr(rruleString)
  } catch (err) {
    console.warn("Failed to parse rrule:", rruleString, err)
    return null
  }
}

/** A rule's occurrences within [rangeStart, rangeEnd] (inclusive), the first `limit`. */
function ruleOccurrences(
  rruleString: string,
  dtstart: Date,
  rangeStart: Date,
  rangeEnd: Date,
  limit: number
): Date[] {
  const rule = parseRRule(rruleString, dtstart)
  if (!rule) return []

  try {
    // The iterator stops the walk at `limit` rather than listing every
    // occurrence of an open-ended rule in a long range and slicing.
    return rule.between(rangeStart, rangeEnd, true, (_date, i) => i < limit)
  } catch (err) {
    console.warn("Failed to expand rrule:", rruleString, err)
    return []
  }
}

/** A human-readable description of a recurrence rule ("every week on Monday"). */
export function describeRRule(
  rruleString: string | null | undefined,
  dtstart: Date | null = null
): string {
  const rule = parseRRule(rruleString, dtstart)
  if (!rule) return "Invalid recurrence rule"

  try {
    return rule.toText()
  } catch (err) {
    console.warn("Failed to describe rrule:", rruleString, err)
    return "Recurring"
  }
}

/** Common recurrence presets for quick selection. */
export const recurrencePresets = [
  { key: "daily", label: "Daily", rrule: "FREQ=DAILY" },
  { key: "weekdays", label: "Weekdays (Mon-Fri)", rrule: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR" },
  { key: "weekly", label: "Weekly", rrule: "FREQ=WEEKLY" },
  { key: "biweekly", label: "Every 2 weeks", rrule: "FREQ=WEEKLY;INTERVAL=2" },
  { key: "monthly", label: "Monthly", rrule: "FREQ=MONTHLY" },
  { key: "yearly", label: "Yearly", rrule: "FREQ=YEARLY" },
]

/**
 * A time's occurrences within the range: a one-off time as-is when it overlaps,
 * a recurring one as one copy per occurrence with that occurrence's start/end
 * (the first occurrence's duration).
 */
function expandTime<T extends RecurringTime>(
  time: T,
  rangeStart: Date,
  rangeEnd: Date,
  limit: number
): ExpandedTime<T>[] {
  // A time with no start has nowhere in the range to go.
  if (!time.start_at) return []

  if (!time.rrule) {
    const startAt = new Date(time.start_at)
    const endAt = time.end_at ? new Date(time.end_at) : startAt
    return startAt <= rangeEnd && endAt >= rangeStart ? [{ ...time, occurrence_index: 0 }] : []
  }

  const dtstart = new Date(time.start_at)
  const duration = time.end_at ? new Date(time.end_at).getTime() - dtstart.getTime() : 0

  return ruleOccurrences(time.rrule, dtstart, rangeStart, rangeEnd, limit).map((date, index) => ({
    ...time,
    // The id stays the series'; the index tells its occurrences apart.
    occurrence_index: index,
    occurrence_date: date,
    start_at: date.toISOString(),
    end_at: duration ? new Date(date.getTime() + duration).toISOString() : null,
    __expanded: true as const,
  }))
}

/**
 * The times that fall in [rangeStart, rangeEnd]: each recurring time expanded
 * into its occurrences in range (at most `limitPerTime` each), each one-off
 * time kept when it overlaps. An unparseable rule yields nothing. Order follows
 * `times`, each series' occurrences in date order.
 *
 * A recurring time's row only carries its first occurrence, so a reader must
 * fetch every recurring time that started before `rangeEnd` — not just the
 * rows overlapping the range — for this to find its occurrences.
 */
export function expandTimes<T extends RecurringTime>(
  times: readonly T[],
  rangeStart: Date,
  rangeEnd: Date,
  limitPerTime = 50
): ExpandedTime<T>[] {
  return times.flatMap((time) => expandTime(time, rangeStart, rangeEnd, limitPerTime))
}
